// src/lib/resources/resource-path.ts
//
// Single source of truth for building a resource's canonical URL path.
//
// The tool/glossary/plural path logic used to be copy-pasted in several places
// — the metadata layer (`getPathFromResource` in
// `~/lib/metadata/resources/resource-fields.ts`), the sitemap provider, and two
// presentational components (`ResourceCard`, `RelatedResources`) — and the
// component copies had drifted: `ResourceCard` pluralized with a naive
// `` `${resourceType}s` `` that emitted the broken `/resources/case-studys/…`
// for case studies, while `RelatedResources` used the `RESOURCE_TYPE_PLURALS`
// map. Both now delegate here, so the path shape can never drift again.
//
// This module is intentionally dependency-light (only the shared enums) so it is
// safe to import into client-component bundles (`ResourceCard` ships `onClick`
// handlers) without dragging the heavy metadata modules along.

import {
  RESOURCE_TYPE_PLURALS,
  type ResourceType,
  type ToolType,
} from '~/lib/resources/types/shared/enums'

/**
 * Minimal structural shape needed to build a resource's URL path.
 *
 * Deliberately narrower than {@link ResourceInstance} so both the discriminated
 * `ResourceInstance` union *and* the broader `BaseResource` (which
 * `ResourceCard` receives) satisfy it — tools carry `toolType`, glossary terms
 * carry `category`, everything else needs only `resourceType` + `slug`.
 */
export interface ResourcePathInput {
  resourceType: ResourceType
  slug: string
  /** Present on tools — selects the `/resources/tools/<plural>` sub-segment. */
  toolType?: ToolType
  /** Glossary category slug — selects `/resources/glossary/<category>`. */
  category?: string
}

/**
 * Builds the canonical resource path **without** a leading slash (e.g.
 * `resources/tools/calculators/roas-calculator`, `resources/case-studies/foo`).
 *
 * This is the exact output {@link getPathFromResource} has always produced; it
 * is the slash-less form because the metadata/structured-data layer composes it
 * into `${siteConfig.url}/${path}`. UI call sites that need an `href` should use
 * {@link resourceHref} instead.
 *
 * @param resource - Minimal resource shape ({@link ResourcePathInput}).
 * @returns The URL path with no leading slash.
 */
export function getResourcePath(resource: ResourcePathInput): string {
  const { resourceType, slug } = resource

  if (resourceType === 'tool') {
    // A valid tool always has a valid `toolType`; if it is missing/unknown we
    // fall back to `/resources/tools/<slug>` (parity with the previous
    // `getPathFromResource` behaviour) rather than emitting a bogus segment.
    const plural = resource.toolType ? RESOURCE_TYPE_PLURALS[resource.toolType] : undefined
    return plural ? `resources/tools/${plural}/${slug}` : `resources/tools/${slug}`
  }

  if (resourceType === 'glossary-term') {
    // `?? 'general'` keeps a category-less term from emitting `.../undefined/...`.
    return `resources/glossary/${resource.category ?? 'general'}/${slug}`
  }

  // guide · template · ebook · case-study → their `RESOURCE_TYPE_PLURALS` entry
  // (case-study → `case-studies`, the fix for the old naive `${type}s` bug).
  return `resources/${RESOURCE_TYPE_PLURALS[resourceType] ?? `${resourceType}s`}/${slug}`
}

/**
 * Builds a resource's `href` — the canonical path with a leading slash — for use
 * in `<Link href>` / `window.location.origin + href`.
 *
 * @param resource - Minimal resource shape ({@link ResourcePathInput}).
 * @returns The absolute-within-site path (e.g. `/resources/guides/foo`).
 */
export function resourceHref(resource: ResourcePathInput): string {
  return `/${getResourcePath(resource)}`
}

/**
 * Convenience wrapper for the common glossary-term case: builds
 * `/resources/glossary/<category>/<slug>` through the shared {@link resourceHref}
 * so the `category ?? 'general'` fallback (and any future route-shape change)
 * stays centralized instead of being hand-concatenated at each call site.
 *
 * @param term - The glossary term's category + slug.
 * @returns The term's canonical href.
 */
export function glossaryTermHref(term: { category?: string; slug: string }): string {
  return resourceHref({ resourceType: 'glossary-term', category: term.category, slug: term.slug })
}
